import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import {
  useCppPartners,
  useDeactivateCppRateCard,
  useDeleteCppRateCard,
  useExpiringCppRateCards,
  useSearchCppRateCards,
} from '~/hooks/apis/costPerPoint';

import type { CppPartner, CppRateCard, CppRateCardStatus } from '~/types/costPerPoint';

import RateCardDialog from './RateCardDialog';
import RateCardTimeline from './RateCardTimeline';
import { formatCpp } from './cppFormat';

/** Selectable rate-card statuses for the Status filter. */
const STATUSES: Array<CppRateCardStatus> = ['DRAFT', 'ACTIVE', 'INACTIVE'];

/** Maps a rate card status to a MUI Chip color. */
function statusColor(status: CppRateCardStatus): 'success' | 'default' | 'warning' {
  if (status === 'ACTIVE') {
    return 'success';
  }
  if (status === 'DRAFT') {
    return 'default';
  }
  return 'warning';
}

/**
 * Whether a rate card's contract has already started (effective start is today or in the past).
 * Mirrors `editability()`'s `started` check in `RateCardDialog.tsx` exactly, so the list and the
 * dialog always agree on whether an ACTIVE card is "future" (not started) or "started".
 */
function isStarted(effectiveStart: string): boolean {
  return dayjs(effectiveStart).isBefore(dayjs()) || dayjs(effectiveStart).isSame(dayjs(), 'day');
}

/**
 * Loyalty-admin page for managing Société Point partner CPP rate cards.
 *
 * Renders a filterable DataGridPro list of rate cards, an expiring-soon banner, and the
 * create/edit/view dialog. Partner names and partner types are resolved from the real
 * partner directory (`useCppPartners`) rather than the rate card itself, since `partnerType`
 * lives on the partner and the card only carries a `partnerId`.
 */
export default function CostPerPointRatesPage() {
  const [partnerFilter, setPartnerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<CppRateCardStatus | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<CppRateCard | null>(null);

  const showConfirmDialog = useConfirmDialog();

  const { data: partnersData, isError: partnersError } = useCppPartners();
  const partners: Array<CppPartner> = useMemo(() => partnersData?.result ?? [], [partnersData]);

  // Real partner directory, keyed by id, for O(1) name/type lookups from each row.
  const partnersById = useMemo(() => {
    const map = new Map<string, CppPartner>();
    for (const partner of partners) {
      map.set(partner.id, partner);
    }
    return map;
  }, [partners]);

  const { data, isLoading, isError } = useSearchCppRateCards({
    ...(partnerFilter && { partnerId: partnerFilter }),
    ...(statusFilter && { status: statusFilter }),
  });
  const { data: expiring } = useExpiringCppRateCards(30);
  const deactivateMutation = useDeactivateCppRateCard();
  const deleteMutation = useDeleteCppRateCard();

  const allCards: Array<CppRateCard> = useMemo(() => data?.result ?? [], [data]);

  const openCreate = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  const openView = (card: CppRateCard) => {
    setSelected(card);
    setDialogOpen(true);
  };

  const handleClearFilters = () => {
    setPartnerFilter('');
    setStatusFilter('');
  };

  const columns: Array<GridColDef<CppRateCard>> = useMemo(
    () => [
      {
        field: 'partnerName',
        headerName: 'Partner Name',
        width: 220,
        valueGetter: (_value, row) => partnersById.get(row.partnerId)?.name ?? row.partnerId,
        renderCell: ({ row }) => (
          <Box>
            <Typography variant="body2">{partnersById.get(row.partnerId)?.name ?? row.partnerId}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.reference}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'partnerType',
        headerName: 'Partner Type',
        width: 130,
        valueGetter: (_value, row) => partnersById.get(row.partnerId)?.partnerType ?? '',
      },
      { field: 'pricingModel', headerName: 'Pricing Model', width: 130 },
      {
        field: 'cpp',
        headerName: 'CPP',
        width: 140,
        valueGetter: (_value, row) => row.cppMinCents,
        renderCell: ({ row }) => formatCpp(row),
      },
      {
        field: 'effectiveStart',
        headerName: 'Effective Start',
        width: 140,
        valueGetter: (_value, row) => row.effectiveStart,
        renderCell: ({ row }) => dayjs(row.effectiveStart).format('DD MMM YYYY'),
      },
      {
        field: 'effectiveEnd',
        headerName: 'Effective End',
        width: 140,
        valueGetter: (_value, row) => row.effectiveEnd ?? '',
        renderCell: ({ row }) => (row.effectiveEnd ? dayjs(row.effectiveEnd).format('DD MMM YYYY') : '-'),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: ({ row }) => <Chip size="small" label={row.status} color={statusColor(row.status)} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            {/* INACTIVE cards are read-only (eye icon), so the tooltip is "View"; all others are editable. */}
            <Tooltip title={row.status === 'INACTIVE' ? 'View' : 'View / Edit'}>
              <IconButton size="small" onClick={() => openView(row)}>
                {row.status === 'INACTIVE' ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {row.status === 'ACTIVE' &&
              (isStarted(row.effectiveStart) ? (
                <Tooltip title="Deactivate">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const name = partnersById.get(row.partnerId)?.name ?? row.partnerId;
                      const ok = await showConfirmDialog({
                        title: 'Deactivate rate card?',
                        description: `This ends ${name} · ${row.reference} today. It can't be reactivated.`,
                      });
                      if (ok) {
                        deactivateMutation.mutate(row.id);
                      }
                    }}
                  >
                    <BlockIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                // Future-start ACTIVE cards haven't started yet, so "deactivating" them just
                // returns them to DRAFT (same endpoint — the backend routes based on start date).
                <Tooltip title="Return to draft">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const name = partnersById.get(row.partnerId)?.name ?? row.partnerId;
                      const ok = await showConfirmDialog({
                        title: 'Return rate card to draft?',
                        description: `${name} · ${row.reference} will move back to Draft.`,
                      });
                      if (ok) {
                        deactivateMutation.mutate(row.id);
                      }
                    }}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ))}
            {row.status === 'DRAFT' && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={async () => {
                    const name = partnersById.get(row.partnerId)?.name ?? row.partnerId;
                    const ok = await showConfirmDialog({
                      title: 'Delete rate card?',
                      description: `This permanently deletes ${name} · ${row.reference}. This cannot be undone.`,
                    });
                    if (ok) {
                      deleteMutation.mutate(row.id);
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [partnersById, deactivateMutation, deleteMutation, showConfirmDialog],
  );

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
        <Box>
          <Typography variant="h4">Société Point Partner Rates</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage transactional unit costs and reward structures across network partners.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>
          Create New Rate Card
        </Button>
      </Stack>

      {partnersError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Partner names could not be loaded; some columns and the partner filter may be incomplete.
        </Alert>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load rate cards. Please try again.
        </Alert>
      )}

      {!!expiring?.result.count && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {(() => {
            const cards = expiring.result.cards ?? [];
            const count = expiring.result.count;
            // Show up to 3 as "Partner Name (RC-ref)"; summarise the rest as "+N more".
            const MAX_NAMED = 3;
            const named = cards
              .slice(0, MAX_NAMED)
              .map((c) => `${partnersById.get(c.partnerId)?.name ?? c.partnerId} (${c.reference})`)
              .join(', ');
            const remaining = count - Math.min(cards.length, MAX_NAMED);
            const suffix = remaining > 0 ? ` +${remaining} more` : '';
            return `Expiring soon — ${count} card${count === 1 ? '' : 's'}${named ? `: ${named}${suffix}` : ''}`;
          })()}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Partner"
          sx={{ minWidth: 200 }}
          value={partnerFilter}
          onChange={(e) => setPartnerFilter(e.target.value)}
        >
          <MenuItem value="">All Partners</MenuItem>
          {partners.map((partner) => (
            <MenuItem key={partner.id} value={partner.id}>
              {partner.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          sx={{ minWidth: 160 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CppRateCardStatus | '')}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={handleClearFilters}>
          Clear Filters
        </Button>
      </Stack>

      <RateCardTimeline cards={allCards} partnersById={partnersById} />

      <Box sx={{ height: 600 }}>
        <DataGridPro
          rows={allCards}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
        />
      </Box>

      <RateCardDialog open={dialogOpen} onClose={() => setDialogOpen(false)} card={selected} />
    </Box>
  );
}
